import axios from 'axios';
import timekeepClient from './timekeepClient';

/**
 * Get employee schedule within a date range
 * @param {string} employeeId - The employee ID
 * @param {string} fromDate - Start date in YYYY-MM-DD format
 * @param {string} toDate - End date in YYYY-MM-DD format
 * @returns {Promise} API response
 */
export const getEmployeeSchedule = async (employeeId, fromDate, toDate) => {
  if (!employeeId || !fromDate || !toDate) {
    throw new Error('Missing required parameters: employeeId, fromDate, toDate');
  }

  const payload = {
    id: employeeId,
    from: fromDate,
    to: toDate
  };

  try {
    const response = await timekeepClient.post('/range/schedule', payload);
    return response.data;
  } catch (error) {
    console.error('Error fetching employee schedule:', error);
    throw error;
  }
};

/**
 * Get breaklist data for a specific store and date range
 * @param {string} storeName - The name of the store
 * @param {string} fromDate - Start date in YYYY-MM-DD format
 * @param {string} toDate - End date in YYYY-MM-DD format
 * @returns {Promise} Breaklist data with transformed structure
 */
export const getBreaklistData = async (storeName, fromDate, toDate) => {
  if (!storeName || !fromDate || !toDate) {
    throw new Error('Missing required parameters: storeName, fromDate, toDate');
  }

  try {
    const response = await timekeepClient.post('/breaklist', {
      store: storeName,
      from: fromDate,
      to: toDate
    });

    if (response.data?.success && response.data?.data) {
      // Transform the data to match our table structure
      const transformedData = response.data.data.map((item, index) => ({
        id: index + 1,
        name: item.empName || item.name || 'Unknown',
        daysWork: parseFloat(item.dayswork || item.days_work || 0).toFixed(2),
        hoursWork: parseInt(item.hourswork || item.hours_work || 0),
        tardinessMinutes: parseInt(item.hourstardy || item.hours_tardy || 0),
        overtimeHours: parseFloat(item.overtime || 0).toFixed(2),
        nightDiffHours: parseFloat(item.nightdiff || item.night_diff || 0).toFixed(2),
        rdWorkHours: parseFloat(item.restday || item.restday_work || 0).toFixed(2),
        legalHolidayHours: parseFloat(item.legalholiday || item.legal_holiday || 0).toFixed(2),
        specialHolidayHours: parseFloat(item.specialholiday || item.special_holiday || 0).toFixed(2)
      }));

      return {
        success: true,
        data: transformedData,
        message: 'Breaklist data loaded successfully'
      };
    } else {
      return {
        success: false,
        data: [],
        message: 'No data found for the selected criteria'
      };
    }
  } catch (error) {
    console.error('Error fetching breaklist data:', error);
    throw error;
  }
};

/**
 * Get all available groups from timekeep API
 * @returns {Promise} List of available groups
 */
export const getGroupsFromTimekeep = async () => {
  try {
    const response = await timekeepClient.get('/groups');
    const groups = Array.isArray(response.data)
      ? response.data
      : response.data?.groups || response.data?.data || [];

    if (groups.length > 0 || response.data?.success) {
      return {
        success: true,
        data: groups,
        message: 'Groups loaded successfully'
      };
    } else {
      return {
        success: false,
        data: [],
        message: 'No groups found'
      };
    }
  } catch (error) {
    console.error('Error fetching groups from timekeep:', error);
    throw error;
  }
};

export const getStoresFromTimekeep = async (groupAccess = []) => {
  try {
    const response = await timekeepClient.post('/stores', {
      groupAccess: groupAccess
    });
    const stores = Array.isArray(response.data)
      ? response.data
      : response.data?.stores || response.data?.data || [];

    if (stores.length > 0 || response.data?.success) {
      return {
        success: true,
        data: stores,
        message: 'Stores loaded successfully'
      };
    } else {
      return {
        success: false,
        data: [],
        message: 'No stores found'
      };
    }
  } catch (error) {
    console.error('Error fetching stores from timekeep:', error);
    throw error;
  }
};

export const getEmployeesByStoreFromTimekeep = async (storeName) => {
  if (!storeName) {
    throw new Error('Missing required parameter: storeName');
  }

  const payload = { store: storeName };
  const fallbackUrl =
    process.env.REACT_APP_TIMEKEEP_ADMIN_USER_STORE_URL ||
    'https://api-admin.sparkletimekeeping.com/api/user/store';
  let lastError;

  try {
    const response = await timekeepClient.post('/user/store', payload);
    return {
      success: true,
      data: Array.isArray(response.data)
        ? response.data
        : response.data?.data || response.data?.users || [],
      message: 'Employees loaded successfully'
    };
  } catch (error) {
    lastError = error;
    console.error('Error fetching employees from configured timekeep API:', error);
  }

  try {
    const response = await axios.post(
      fallbackUrl,
      payload,
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );

    return {
      success: true,
      data: Array.isArray(response.data)
        ? response.data
        : response.data?.data || response.data?.users || [],
      message: 'Employees loaded successfully'
    };
  } catch (error) {
    console.error('Error fetching employees from fallback timekeep API:', error);
    throw lastError || error;
  }
};

/**
 * Get approved breaklists from timekeep API with user permissions
 * @param {Object} params - Parameters for the request
 * @param {number} params.payroll - User ID
 * @param {string} params.from - Start date in YYYY-MM-DD format
 * @param {string} params.to - End date in YYYY-MM-DD format
 * @param {Array} params.storeAccess - Array of store names user can access
 * @param {Array} params.groupAccess - Array of group names user can access
 * @returns {Promise} Approved breaklists data
 */
export const getApprovedBreaklists = async (params) => {
  if (!params.payroll || !params.from || !params.to) {
    throw new Error('Missing required parameters: payroll, from, to');
  }

  try {
    const response = await timekeepClient.post('/list/breaklistapproved/payroll', {
      payroll: params.payroll,
      from: params.from,
      to: params.to,
      storeAccess: params.storeAccess || [],
      groupAccess: params.groupAccess || []
    });

    if (response.data?.success) {
      return {
        success: true,
        data: response.data.data || [],
        message: 'Approved breaklists loaded successfully'
      };
    } else {
      return {
        success: false,
        data: [],
        message: response.data?.message || 'Failed to load approved breaklists'
      };
    }
  } catch (error) {
    console.error('Error fetching approved breaklists:', error);
    throw error;
  }
};

/**
 * Get all holidays from timekeep API and filter by date range
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @returns {Promise} Filtered holidays within the date range
 */
export const getHolidaysFromTimekeep = async (startDate, endDate, store) => {
  if (!startDate || !endDate) {
    throw new Error('Missing required parameters: startDate, endDate');
  }

  try {
    const response = await timekeepClient.get('/holidays');
    const storeResponse = await timekeepClient.post('/store/group', { store: store });
    if (response.data?.success && response.data?.holidays) {
      // Filter holidays based on the date range
      const start = new Date(startDate);
      const end = new Date(endDate);

      const filteredHolidays = response.data.holidays.filter(holiday => {

        if (holiday.store && holiday.store.length > 0) {
          if (!holiday.store.includes(store)) {
            return false;
          }

          const holidayDate = new Date(holiday.date);
          return holidayDate >= start && holidayDate <= end;
        } else if (holiday.group && holiday.group.length > 0) {
          if (!holiday.group.includes(storeResponse.data.group)) {
            return false;
          }

          const holidayDate = new Date(holiday.date);
          return holidayDate >= start && holidayDate <= end;
        } else {
          const holidayDate = new Date(holiday.date);
          return holidayDate >= start && holidayDate <= end;
        }
      });
      // console.log('check this', filteredHolidays);
      return {
        success: true,
        data: filteredHolidays
      };
    } else {
      return {
        success: false,
        data: [],
        message: 'Invalid response format from holidays API'
      };
    }
  } catch (error) {
    console.error('Error fetching holidays from timekeep:', error);
    throw error;
  }
};
